import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { InventoryItem, InventoryCategory } from '../QuickSaleScreen';
import salesService from '../../../../services/salesService';
import { InventoryService } from '../../../accounting/inventory/services/inventoryService';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../../../../config/brand';
import { clubEntryPackageService, ClubEntryPackageDto } from '../../../../services/clubEntryPackageService';

// FilteredProduct Interface
export interface FilteredProduct {
  id: string;
  name: string;
  price: number;
  category: string;
  image?: string; // Optional - önbellekte yoksa undefined olabilir
  barcode?: string;
  unit?: string;
  // ✅ Kulüp girişi paketi (sanal ürün): tıklanınca içindeki ürünler sepete eklenir
  isBundle?: boolean;
  color?: string;
}

// ScreenSettings interface (sadece ihtiyaç duyulan kısımlar)
interface StockVisibilitySettings {
  warehouses?: string[];
  categories?: { [warehouseId: string]: string[] };
  products?: { [warehouseId: string]: { [categoryId: string]: string[] } };
}

interface UseQuickSaleProductsProps {
  allInventoryItems: InventoryItem[];
  allCategoriesData: InventoryCategory[];
  allWarehouses: any[];
  screenSettings: any; // ScreenSettings type
  companyId: string | null;
  showProductImages: boolean;
  handleRemoveDiscount: () => void;
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  setTempSelectedProduct: (product: any) => void;
  setIsWeightInputModalOpen: (open: boolean) => void;
  setCart: React.Dispatch<React.SetStateAction<any[]>>;
  focusSearchBar?: () => void; // ✅ YENİ: Kategori değiştirilince fokuslan
  branchId?: string | null; // ✅ YENİ: Şube bazlı fiyatlandırma için şube ID
  // ✅ YENİ: Menü opsiyon modalı için
  setPendingMenuProduct?: (product: any) => void;
  setIsMenuOptionModalOpen?: (open: boolean) => void;
  showAllCategory?: boolean; // ✅ YENİ: "Tümü" kategorisi gösterilsin mi (varsayılan true)
  isPackageMode?: boolean; // ✅ YENİ: Paket modu aktif ise packagePrice kullanılacak
  // ✅ YENİ: Hızlı Satış ekran ayarı (Depo/Stok tab > Şube Paket Fiyatı Kullan).
  // isPackageMode gibi şube paket fiyatını önceliklendirir; paket fiyatı
  // yoksa şube salePrice'a fallback yapar.
  usePackagePrices?: boolean;
  // ✅ YENİ: Kulüp girişi paket ürünleri
  screenId?: number | null;
  isClubEntryMode?: boolean;
}

interface UseQuickSaleProductsReturn {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  displayableCategories: InventoryCategory[];
  filteredProducts: FilteredProduct[];
  addToCart: (product: any, quantity?: number) => void;
  addToCartWithOptions: (pendingProduct: any, selectedOptions: any[], totalPriceDifference: number, normalItems?: any[]) => void; // ✅ YENİ
  productImagesCache: {[productId: string]: string};
  selectedWarehouseIds: string[];
  setSelectedWarehouseIds: (ids: string[]) => void;
  activeDisplayWarehouseId: string | null;
  setActiveDisplayWarehouseId: (id: string | null) => void;
  handleCategoryChange: (event: React.SyntheticEvent, newValue: string) => void;
  loadAndCacheProductImages: () => void;
  sqlInventoryItems: InventoryItem[];
  getScreenCategoryId: (productId: string) => string;
  resolveBranchPrice: (productId: string) => Promise<{ price: number; vatStatus: 'included' | 'excluded'; usedBranchPrice: boolean } | null>;
}

export const useQuickSaleProducts = ({
  allInventoryItems,
  allCategoriesData,
  allWarehouses,
  screenSettings,
  companyId,
  showProductImages,
  handleRemoveDiscount,
  showSnackbar,
  setTempSelectedProduct,
  setIsWeightInputModalOpen,
  setCart,
  focusSearchBar, // ✅ YENİ
  branchId, // ✅ YENİ: Şube bazlı fiyatlandırma
  // ✅ YENİ: Menü opsiyon modalı için
  setPendingMenuProduct,
  setIsMenuOptionModalOpen,
  showAllCategory = true, // ✅ YENİ: "Tümü" kategorisi gösterilsin mi (varsayılan true)
  isPackageMode = false, // ✅ YENİ: Paket modu aktif ise packagePrice kullanılacak
  usePackagePrices = false, // ✅ YENİ: Ekran ayarı - şube paket fiyatını kullan
  screenId = null, // ✅ YENİ: Kulüp girişi paketleri için ekran ID
  isClubEntryMode = false, // ✅ YENİ: Kulüp girişi modu aktif mi
}: UseQuickSaleProductsProps): UseQuickSaleProductsReturn => {
  // ✅ YENİ: Paket modu YA DA ekran ayarı açıksa paket fiyatı öncelikli
  const shouldUsePackagePrice = isPackageMode || usePackagePrices;
  const { t } = useTranslation('salesSystem');

  const getLocalizedCategoryName = useCallback((categoryName: string): string => {
    const normalizedName = categoryName.trim().toLocaleLowerCase('tr-TR');
    const aliases: Record<string, string> = {
      'giriş': t('quickSaleScreen.categoryAliases.entry'),
      'giris': t('quickSaleScreen.categoryAliases.entry'),
      'çıkış': t('quickSaleScreen.categoryAliases.exit'),
      'cikis': t('quickSaleScreen.categoryAliases.exit'),
    };

    return aliases[normalizedName] || categoryName;
  }, [t]);

  // State declarations
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [displayableCategories, setDisplayableCategories] = useState<InventoryCategory[]>([]);
  const [productImagesCache, setProductImagesCache] = useState<{[productId: string]: string}>({});
  const [imagesCacheTimestamp, setImagesCacheTimestamp] = useState<number>(0);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>([]);
  const [activeDisplayWarehouseId, setActiveDisplayWarehouseId] = useState<string | null>(null);
  
  // ✅ YENİ: SQL'den çekilen ürünler
  const [sqlInventoryItems, setSqlInventoryItems] = useState<InventoryItem[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // ✅ YENİ: Şube fiyatları cache (inventoryItemId -> BranchPrice)
  const [branchPricesCache, setBranchPricesCache] = useState<{ [inventoryItemId: string]: any }>({});
  const [loadingBranchPrices, setLoadingBranchPrices] = useState(false);

  // ✅ YENİ: Kulüp girişi paketleri ve paket-içi ürün detayları (enrich/fiyat için)
  const [clubEntryPackages, setClubEntryPackages] = useState<ClubEntryPackageDto[]>([]);
  const [bundleProductMap, setBundleProductMap] = useState<{ [idLower: string]: InventoryItem }>({});
  const clubEntryPackagesRef = useRef<ClubEntryPackageDto[]>([]);
  useEffect(() => { clubEntryPackagesRef.current = clubEntryPackages; }, [clubEntryPackages]);
  // addToCart recursion (paket genişletme) için stabil referans
  const addToCartRef = useRef<(product: any, quantity?: number, skipOptionModals?: boolean, clubGroup?: { groupId: string; packageId: number; packageName: string }) => void>(() => {});
  
  // ✅ DUPLICATE ENGELLEME: API çağrısı yapılıyor mu?
  const isLoadingRef = useRef(false);
  const lastLoadedIdsRef = useRef<string>('');
  const lastBranchIdRef = useRef<string | null>(null);
  
  // Ekran ayarlarındaki kategori eşlemesi (kategori bazlı yazdırma için)
  const productScreenCategoryMapRef = useRef<{[productId: string]: string}>({});
  
  // ✅ FIX: branchId değiştiğinde ref'i sıfırla (yeni şube için fiyatlar yüklensin)
  useEffect(() => {
    if (branchId !== lastBranchIdRef.current) {
      console.log(`🔄 [ŞUBE FİYAT] Şube değişti: ${lastBranchIdRef.current} → ${branchId}, cache sıfırlanıyor`);
      lastBranchIdRef.current = null; // Ref'i sıfırla, useEffect tekrar çalışsın
      setBranchPricesCache({}); // Cache'i temizle
    }
  }, [branchId]);

  // ✅ PERFORMANS: Ürünleri cache'den veya API'den yükle
  useEffect(() => {
    const loadProductsFromSQL = async () => {
      const startTime = performance.now();
      
      if (!companyId || !screenSettings?.stockVisibility?.products) {
        setSqlInventoryItems([]);
        return;
      }

      // StockVisibilityJson'dan tüm ürün ID'lerini topla
      const allProductIds: string[] = [];
      const productsSettings = screenSettings.stockVisibility.products;
      
      Object.values(productsSettings).forEach((warehouseProducts: any) => {
        Object.values(warehouseProducts).forEach((categoryProducts: any) => {
          if (Array.isArray(categoryProducts)) {
            allProductIds.push(...categoryProducts);
          }
        });
      });

      // Unique ID'leri al
      const uniqueProductIds = Array.from(new Set(allProductIds));
      
      if (uniqueProductIds.length === 0) {
        setSqlInventoryItems([]);
        return;
      }
      
      // ✅ DUPLICATE ENGELLEME: Aynı ID'ler için tekrar yükleme yapma
      const idsHash = JSON.stringify(uniqueProductIds.sort());
      if (isLoadingRef.current || lastLoadedIdsRef.current === idsHash) {
        return;
      }
      
      isLoadingRef.current = true;
      setLoadingProducts(true);

      try {
        // ✅ CACHE KALDIRILDI: Her sayfa açılışında SQL'den güncel veri çekiliyor
        // Stok yönetiminden yapılan değişiklikler anında yansısın diye localStorage cache kullanmıyoruz
        
        // ✅ ESKİ CACHE'LERİ TEMİZLE (v1, v2, v3)
        try {
          const oldKeys = ['v1', 'v2', 'v3', ''].flatMap(v => [
            `quicksale_products_${v ? v + '_' : ''}${companyId}`,
            `quicksale_products_timestamp_${v ? v + '_' : ''}${companyId}`,
            `quicksale_products_ids_${v ? v + '_' : ''}${companyId}`,
          ]);
          oldKeys.forEach(key => localStorage.removeItem(key));
        } catch (e) { /* ignore */ }
        
        console.log(`🔄 [ÜRÜNLER] API'den yükleniyor: ${uniqueProductIds.length} ürün...`);
        const apiStartTime = performance.now();
        const sqlProducts = await salesService.getProductsByIds(uniqueProductIds);
        
        // SQL'den gelen ürünleri InventoryItem formatına çevir
        const inventoryItems: InventoryItem[] = sqlProducts.map((item: any) => ({
          id: item.id?.toString() || item.Id?.toString() || '',
          name: item.name || item.Name || '',
          salePrice: item.salePrice || item.SalePrice || 0,
          categoryId: item.categoryId?.toString() || item.CategoryId?.toString() || '',
          warehouseId: item.warehouseId?.toString() || item.WarehouseId?.toString() || '',
          quantity: item.totalQuantity || item.TotalQuantity || 0,
          unit: item.unit || item.Unit || 'adet',
          barcode: item.barcode || item.Barcode || '',
          imageUrl: item.thumbnailUrl || item.ThumbnailUrl || item.imageUrl || item.ImageUrl || null,
          vatRate: item.vatRate || item.VatRate || 0,
          vatStatus: item.vatStatus || item.VatStatus || 'excluded',
          canBeNegative: item.canBeNegative ?? item.CanBeNegative ?? true,
          departmentNo: item.departmentNo || item.DepartmentNo || null,
          stockCode: item.stockCode || item.StockCode || '',
          // ✅ Şube bazlı fiyatlandırma
          useBranchPricing: item.useBranchPricing ?? item.UseBranchPricing ?? false,
          // ✅ YENİ: Reçete ve Menü Bilgileri
          hasRecipe: item.hasRecipe ?? item.HasRecipe ?? false,
          hasMenu: item.hasMenu ?? item.HasMenu ?? false,
          itemType: item.itemType || item.ItemType || 'PRODUCT',
        }));
        
        // ✅ DEBUG: API'den gelen useBranchPricing değerlerini logla
        const withBranchPricing = inventoryItems.filter(p => p.useBranchPricing === true);
        console.log(`✅ [ÜRÜNLER] API'den yüklendi: ${inventoryItems.length} ürün, useBranchPricing=true: ${withBranchPricing.length} (${Math.round(performance.now() - apiStartTime)}ms)`);
        if (withBranchPricing.length > 0) {
          console.log(`📊 [API DEBUG] useBranchPricing=true olan ürünler:`, withBranchPricing.map(p => ({ id: p.id, name: p.name, useBranchPricing: p.useBranchPricing })));
        }
        
        setSqlInventoryItems(inventoryItems);
        lastLoadedIdsRef.current = idsHash; // Başarılı yüklemede ID'leri kaydet
      } catch (error) {
        console.error(`❌ [ÜRÜNLER] Yükleme hatası:`, error);
        showSnackbar(`Ürünler yüklenirken bir hata oluştu`, 'error');
        setSqlInventoryItems([]);
      } finally {
        setLoadingProducts(false);
        isLoadingRef.current = false; // Lock'u serbest bırak
      }
    };

    loadProductsFromSQL();
  }, [companyId, screenSettings?.stockVisibility]);

  // ✅ YENİ: Kulüp girişi paketlerini ve paket-içi ürün detaylarını yükle
  useEffect(() => {
    const loadPackages = async () => {
      if (!isClubEntryMode || !screenId || !companyId) {
        setClubEntryPackages([]);
        setBundleProductMap({});
        return;
      }
      try {
        const packages = await clubEntryPackageService.getPackages(screenId);
        setClubEntryPackages(packages);

        // Paket-içi ürün ID'lerini topla → tam detaylarını çek (tüm ürünlerden seçilebildiği
        // için ekranın stockVisibility'sinde olmayabilirler) → enrich için sakla
        const productIds = Array.from(new Set(
          packages.flatMap(p => p.items.map(i => i.productId).filter((id): id is string => !!id))
        ));
        if (productIds.length === 0) {
          setBundleProductMap({});
          return;
        }
        const sqlProducts = await salesService.getProductsByIds(productIds);
        const map: { [idLower: string]: InventoryItem } = {};
        sqlProducts.forEach((item: any) => {
          const id = (item.id?.toString() || item.Id?.toString() || '');
          if (!id) return;
          map[id.toLowerCase()] = {
            id,
            name: item.name || item.Name || '',
            salePrice: item.salePrice || item.SalePrice || 0,
            categoryId: item.categoryId?.toString() || item.CategoryId?.toString() || '',
            warehouseId: item.warehouseId?.toString() || item.WarehouseId?.toString() || '',
            quantity: item.totalQuantity || item.TotalQuantity || 0,
            unit: item.unit || item.Unit || 'adet',
            barcode: item.barcode || item.Barcode || '',
            imageUrl: item.thumbnailUrl || item.ThumbnailUrl || item.imageUrl || item.ImageUrl || null,
            vatRate: item.vatRate || item.VatRate || 0,
            vatStatus: item.vatStatus || item.VatStatus || 'excluded',
            canBeNegative: item.canBeNegative ?? item.CanBeNegative ?? true,
            departmentNo: item.departmentNo || item.DepartmentNo || null,
            stockCode: item.stockCode || item.StockCode || '',
            useBranchPricing: item.useBranchPricing ?? item.UseBranchPricing ?? false,
            hasRecipe: item.hasRecipe ?? item.HasRecipe ?? false,
            hasMenu: item.hasMenu ?? item.HasMenu ?? false,
            itemType: item.itemType || item.ItemType || 'PRODUCT',
          } as InventoryItem;
        });
        setBundleProductMap(map);
      } catch (error) {
        console.error('[KULÜP PAKET] Paketler yüklenemedi:', error);
        setClubEntryPackages([]);
        setBundleProductMap({});
      }
    };
    loadPackages();
  }, [isClubEntryMode, screenId, companyId]);

  // ✅ YENİ: Şube fiyatlarını yükle (ürünler yüklendikten sonra)
  useEffect(() => {
    const loadBranchPrices = async () => {
      console.log('🔄 [ŞUBE FİYAT - useEffect] Trigger edildi:', {
        companyId: companyId ? '✅' : '❌',
        branchId: branchId || '❌ YOK',
        sqlInventoryItemsLength: sqlInventoryItems.length,
        lastBranchIdRef: lastBranchIdRef.current
      });
      
      // Şube ID yoksa veya ürün yoksa yükleme
      if (!companyId || !branchId || sqlInventoryItems.length === 0) {
        console.log('⏭️ [ŞUBE FİYAT] Erken dönüş - eksik parametre');
        setBranchPricesCache({});
        return;
      }

      // Aynı şube için tekrar yükleme yapma (ref ile kontrol)
      if (lastBranchIdRef.current === branchId) {
        console.log('⏭️ [ŞUBE FİYAT] Aynı şube için zaten yüklendi, atlanıyor');
        return;
      }

      // useBranchPricing = true olan ürünlerin ID'lerini topla
      const itemsWithBranchPricing = sqlInventoryItems.filter(item => (item as any).useBranchPricing === true);
      
      console.log(`🔍 [ŞUBE FİYAT] useBranchPricing kontrolü:`, {
        toplamUrun: sqlInventoryItems.length,
        useBranchPricingAcikUrun: itemsWithBranchPricing.length,
        ornekler: itemsWithBranchPricing.slice(0, 3).map(i => ({ id: i.id, name: i.name, useBranchPricing: (i as any).useBranchPricing }))
      });
      
      if (itemsWithBranchPricing.length === 0) {
        console.log('📊 [ŞUBE FİYAT] useBranchPricing açık ürün yok');
        setBranchPricesCache({});
        lastBranchIdRef.current = branchId; // ✅ FIX: Ref'i güncelle ki tekrar çağrılmasın
        return;
      }

      const itemIds = itemsWithBranchPricing.map(item => item.id);
      console.log(`📊 [ŞUBE FİYAT] ${itemIds.length} ürün için şube fiyatları yükleniyor... (Şube: ${branchId})`);

      setLoadingBranchPrices(true);
      try {
        const prices = await InventoryService.getBulkBranchPrices(companyId, branchId, itemIds);
        setBranchPricesCache(prices);
        lastBranchIdRef.current = branchId;
        console.log(`✅ [ŞUBE FİYAT] ${Object.keys(prices).length} şube fiyatı yüklendi:`, prices);
      } catch (error) {
        console.error('❌ [ŞUBE FİYAT] Şube fiyatları yüklenirken hata:', error);
        setBranchPricesCache({});
      } finally {
        setLoadingBranchPrices(false);
      }
    };

    loadBranchPrices();
  }, [companyId, branchId, sqlInventoryItems]);

  // Aktif Görüntüleme Deposunu ve Görüntülenebilir Kategorileri Ayarla
  useEffect(() => {
    if (screenSettings && allCategoriesData.length > 0 && allWarehouses.length > 0) {
      let categoriesToDisplay: InventoryCategory[] = [];
      const allOption: InventoryCategory = { id: 'all', name: t('quickSaleScreen.categoryTabs.all') }; 

      // Ayarlarda seçili depo ID'lerini al
      const selectedWarehouseIds = screenSettings.stockVisibility?.warehouses || [];

      // Tüm seçili depolardaki ayarlı kategori ID'lerini topla
      const uniqueConfiguredCategoryIds = new Set<string>();
      if (selectedWarehouseIds.length > 0 && screenSettings.stockVisibility?.categories) {
        selectedWarehouseIds.forEach((whId: string) => {
          const categoryIdsForWarehouse = screenSettings.stockVisibility?.categories?.[whId]; 
          if (categoryIdsForWarehouse && categoryIdsForWarehouse.length > 0) {
            categoryIdsForWarehouse.forEach((catId: string) => uniqueConfiguredCategoryIds.add(catId));
          }
        });
      }

      // ✅ Kategorileri kullanıcının belirlediği sıraya göre göster
      if (uniqueConfiguredCategoryIds.size > 0) {
        const orderedCategoryIds: string[] = [];
        const seenIds = new Set<string>();
        
        selectedWarehouseIds.forEach((warehouseId: string) => {
          const categoryIdsForWarehouse = screenSettings.stockVisibility.categories?.[warehouseId] || [];
          categoryIdsForWarehouse.forEach((catId: string) => {
            if (!seenIds.has(catId)) {
              orderedCategoryIds.push(catId);
              seenIds.add(catId);
            }
          });
        });
        
        categoriesToDisplay = orderedCategoryIds
          .map(catId => allCategoriesData.find(cat => cat.id === catId))
          .filter((cat): cat is InventoryCategory => !!cat)
          .map(cat => ({ ...cat, name: getLocalizedCategoryName(cat.name) }));
        
        // ✅ YENİ: showAllCategory ayarına göre "Tümü" kategorisini göster/gizle
        if (showAllCategory) {
          setDisplayableCategories([allOption, ...categoriesToDisplay]);
        } else {
          setDisplayableCategories(categoriesToDisplay);
          // "Tümü" kapalıysa ve şu an "all" seçiliyse ilk kategoriye geç
          if (selectedCategory === 'all' && categoriesToDisplay.length > 0) {
            setSelectedCategory(categoriesToDisplay[0].id);
          }
        }
      } else {
        // Kategori yoksa sadece showAllCategory true ise "Tümü" göster
        if (showAllCategory) {
          setDisplayableCategories([allOption]); 
        } else {
          setDisplayableCategories([]); 
        }
      }
    } else {
      // Ayarlar yüklenmemişse varsayılan olarak showAllCategory'ye göre ayarla
      if (showAllCategory) {
        setDisplayableCategories([{ id: 'all', name: t('quickSaleScreen.categoryTabs.all') }]); 
      } else {
        setDisplayableCategories([]); 
      }
    }
  }, [screenSettings, allCategoriesData, allWarehouses, showAllCategory, selectedCategory, t, getLocalizedCategoryName]);

  // ❌ ESKİ useEffect SİLİNDİ - Yukarıdaki useEffect zaten kategorileri doğru sırayla yüklüyor

  // ✅ YENİ: Bir paket-içi ürünün detayını bul (önce ekran ürünleri, sonra paket-detay cache)
  const findProductDetail = useCallback((productId?: string | null): InventoryItem | undefined => {
    if (!productId) return undefined;
    const lower = productId.toLowerCase();
    const fromSql = sqlInventoryItems.find(it => it.id.toLowerCase() === lower);
    return fromSql || bundleProductMap[lower];
  }, [sqlInventoryItems, bundleProductMap]);
  // addToCart içinden deps'e dokunmadan erişmek için ref
  const findProductDetailRef = useRef(findProductDetail);
  findProductDetailRef.current = findProductDetail;

  // ✅ YENİ: Kulüp girişi paketlerini "sanal ürün" kartlarına çevir
  // (fiyat = içindeki ürünlerin salePrice x adet toplamı)
  const bundleVirtualProducts: FilteredProduct[] = useMemo(() => {
    if (!isClubEntryMode || clubEntryPackages.length === 0) return [];
    return clubEntryPackages.map(pkg => {
      const total = pkg.items.reduce((sum, it) => {
        const detail = findProductDetail(it.productId);
        return sum + (detail?.salePrice || 0) * (it.quantity || 0);
      }, 0);
      return {
        id: `BUNDLE-${pkg.id}`,
        name: pkg.name,
        price: total,
        category: '',
        image: BRAND.logos.main,
        isBundle: true,
        color: pkg.color || undefined,
      } as FilteredProduct;
    });
  }, [isClubEntryMode, clubEntryPackages, findProductDetail]);

  // filteredProducts artık sadece seçili kategoriye ve ayarlara göre filtrelenecek
  // ✅ YENİ: SQL'den çekilen ürünleri kullan (allInventoryItems yerine)
  // ✅ FIX: Ürünleri EKRAN AYARLARINDA belirlenen kategorilere göre göster (orijinal kategori değil!)
  const filteredProducts: FilteredProduct[] = useMemo(() => {
    const selectedWarehouseIds = screenSettings?.stockVisibility?.warehouses || [];

    // ✅ SQL ürünlerini kullan
    if (!screenSettings || sqlInventoryItems.length === 0 || selectedWarehouseIds.length === 0 || displayableCategories.length === 0) {
      // ✅ KULÜP GİRİŞİ: Ekranda normal ürün/kategori/depo tanımlı olmasa bile
      // "Tümü" görünümünde paketler (BUNDLE) gösterilmeli (sadece paket tanımlı ekran senaryosu).
      if (isClubEntryMode && selectedCategory === 'all' && bundleVirtualProducts.length > 0) {
        return [...bundleVirtualProducts];
      }
      return [] as FilteredProduct[];
    }
    
    // ✅ FIX: Ürün ID'lerini SIRAYLA topla ve EKRAN AYARLARINDAKI kategorisini de kaydet
    const orderedProductIds: string[] = [];
    const productIdSet = new Set<string>();
    const productScreenCategoryMap: { [productId: string]: string } = {}; // ✅ YENİ: Ekran ayarlarındaki kategori
    const allProductSettings = screenSettings.stockVisibility?.products || {};
    const categoriesSettings = screenSettings.stockVisibility?.categories || {};

    if (allProductSettings) {
      // Kategorileri ekran ayarlarındaki sıraya göre al
      const orderedCategoryIds: string[] = [];
      selectedWarehouseIds.forEach((warehouseId: string) => {
        const categoryIdsForWarehouse = categoriesSettings[warehouseId] || [];
        categoryIdsForWarehouse.forEach((catId: string) => {
          if (!orderedCategoryIds.includes(catId)) {
            orderedCategoryIds.push(catId);
          }
        });
      });

      // Seçili tüm depoları döngüye al
      selectedWarehouseIds.forEach((warehouseId: string) => {
        const productSettingsForCurrentWarehouse = allProductSettings[warehouseId];
        
        if (productSettingsForCurrentWarehouse) {
          if (selectedCategory !== 'all') {
            // Belirli bir kategori seçili - o kategorideki ürünleri sırasıyla ekle
            const idsForSelectedCategory = productSettingsForCurrentWarehouse[selectedCategory];
            
            if (idsForSelectedCategory && idsForSelectedCategory.length > 0) {
              idsForSelectedCategory.forEach((id: string) => {
                if (!productIdSet.has(id)) {
                  productIdSet.add(id);
                  orderedProductIds.push(id);
                  productScreenCategoryMap[id] = selectedCategory;
                }
              });
            }
          } else {
            // "Tümü" kategorisi seçili: Kategorileri SIRAYLA tara, her kategorideki ürünleri sırasıyla ekle
            orderedCategoryIds.forEach((categoryId: string) => {
              const idsForCategory = productSettingsForCurrentWarehouse[categoryId];
              if (idsForCategory && Array.isArray(idsForCategory)) {
                idsForCategory.forEach((id: string) => {
                  if (!productIdSet.has(id)) {
                    productIdSet.add(id);
                    orderedProductIds.push(id);
                    productScreenCategoryMap[id] = categoryId;
                  }
                  // Duplicate ürünler sessizce atlanır (ilk eklendiği kategoride kalır)
                });
              }
            });
          }
        }
      });
    }

    if (orderedProductIds.length === 0) {
      // ✅ KULÜP GİRİŞİ: Ekranda ürün ayarı olmasa da (örn. pakete tıklayınca paket içi ürünler
      // sqlInventoryItems'a eklenince bu dala düşülebilir) "Tümü" görünümünde paketler korunmalı.
      if (isClubEntryMode && selectedCategory === 'all' && bundleVirtualProducts.length > 0) {
        return [...bundleVirtualProducts];
      }
      return [] as FilteredProduct[];
    }

    // ✅ SQL'den çekilen ürünleri ID sırasına göre düzenle
    const productMap = new Map<string, typeof sqlInventoryItems[0]>();
    sqlInventoryItems.forEach(item => productMap.set(item.id, item));
    
    // ✅ FIX: Ürünleri ekran ayarlarındaki sıraya göre al
    const orderedItems = orderedProductIds
      .map(id => productMap.get(id))
      .filter((item): item is typeof sqlInventoryItems[0] => !!item);

    // ✅ FIX: Ürünleri EKRAN AYARLARINDAKI kategoriye göre sırala (orijinal kategori değil!)
    const categoryOrder = new Map<string, number>();
    displayableCategories.forEach((cat, index) => categoryOrder.set(cat.id, index));

    const sortedItems = [...orderedItems].sort((a, b) => {
      // ✅ FIX: Ekran ayarlarındaki kategoriyi kullan (orijinal categoryId değil!)
      const screenCatA = productScreenCategoryMap[a.id] || '';
      const screenCatB = productScreenCategoryMap[b.id] || '';
      const catOrderA = categoryOrder.get(screenCatA) ?? 999;
      const catOrderB = categoryOrder.get(screenCatB) ?? 999;
      
      if (catOrderA !== catOrderB) {
        return catOrderA - catOrderB;
      }
      
      // Aynı kategorideyse alfabetik sırala (Türkçe karakter desteği)
      return (a.name || '').localeCompare(b.name || '', 'tr', { sensitivity: 'base' });
    });

    const result = sortedItems.map(p => {
      // Şube fiyatı varsa onu kullan
      let displayPrice = p.salePrice || 0;
      
      if (p.useBranchPricing === true) {
        // Şube fiyatını kontrol et (küçük/büyük harf duyarsız ID kontrolü)
        const branchPrice = branchPricesCache[p.id?.toLowerCase()] || branchPricesCache[p.id];
        if (branchPrice) {
          // ✅ PAKET MODU veya EKRAN AYARI: packagePrice varsa onu kullan, yoksa salePrice
          if (shouldUsePackagePrice) {
            const branchPackagePrice = branchPrice.packagePrice || branchPrice.PackagePrice;
            if (branchPackagePrice && branchPackagePrice > 0) {
              displayPrice = branchPackagePrice;
              console.log(`📦 [PAKET FİYAT] "${p.name}" için paket fiyatı kullanılıyor: ${branchPackagePrice} TL (isPackageMode=${isPackageMode}, usePackagePrices=${usePackagePrices})`);
            } else {
              // Paket fiyatı yoksa salePrice'a fallback
              const branchSalePrice = branchPrice.salePrice || branchPrice.SalePrice;
              if (branchSalePrice) {
                displayPrice = branchSalePrice;
                console.log(`⚠️ [PAKET FİYAT] "${p.name}" için paket fiyatı yok, salePrice kullanılıyor: ${branchSalePrice} TL`);
              }
            }
          } else {
            // Normal mod: salePrice kullan
            const branchSalePrice = branchPrice.salePrice || branchPrice.SalePrice;
            if (branchSalePrice) {
              displayPrice = branchSalePrice;
            }
          }
        }
      }
      
      // Kategori adını EKRAN AYARLARINDAN al
      const screenCategoryId = productScreenCategoryMap[p.id] || '';
      const screenCategoryName = getLocalizedCategoryName(
        allCategoriesData.find(c => c.id === screenCategoryId)?.name || t('quickSaleScreen.categoryTabs.other')
      );
      
      return {
        id: p.id,
        name: p.name,
        price: displayPrice,
        category: screenCategoryName,
        image: productImagesCache[p.id] || p.imageUrl || p.imageFromInventory || BRAND.logos.main,
        barcode: p.barcode,
        unit: p.unit
      };
    });
    
    // Ref'i güncelle - addToCart'ta kategori bazlı yazdırma için kullanılacak
    productScreenCategoryMapRef.current = productScreenCategoryMap;
    
    // ✅ YENİ: Kulüp girişi modunda "Tümü" görünümünde paketleri en başa ekle
    if (isClubEntryMode && selectedCategory === 'all' && bundleVirtualProducts.length > 0) {
      return [...bundleVirtualProducts, ...result];
    }
    return result;
  }, [
    selectedCategory,
    sqlInventoryItems, // ✅ allInventoryItems yerine
    allCategoriesData,
    screenSettings,
    displayableCategories,
    productImagesCache,
    branchPricesCache, // ✅ YENİ: Şube fiyatları değiştiğinde de yeniden hesapla
    isPackageMode, // ✅ YENİ: Paket modu değiştiğinde yeniden hesapla
    usePackagePrices, // ✅ YENİ: Ekran ayarı değiştiğinde yeniden hesapla
    shouldUsePackagePrice,
    isClubEntryMode, // ✅ YENİ: Kulüp girişi modu
    bundleVirtualProducts, // ✅ YENİ: Paket sanal ürünleri
  ]);

  // addToCart fonksiyonu - SQL'den çekilen ürünleri kullan
  const addToCart = useCallback((product: any, quantity: number = 1, skipOptionModals: boolean = false, clubGroup?: { groupId: string; packageId: number; packageName: string }) => {
    // ✅ KULÜP GİRİŞİ PAKETİ: "BUNDLE-{id}" tıklanınca paketin kendisi DEĞİL,
    // içindeki ürünler tek tek sepete eklenir (her biri normal ürün gibi: KDV/stok/fiş).
    // Paket içeriği doğrudan eklenir; menü/tartı opsiyon modalları AÇILMAZ (skipOptionModals=true).
    // Her tıklama bir "grup" (= bir kişi/kart) olur; içindeki kalemler ortak clubGroupId alır,
    // böylece ödeme sonrası kart atamada hangi ürünün hangi karta gideceği bilinir.
    if (typeof product?.id === 'string' && product.id.startsWith('BUNDLE-')) {
      const pkgId = parseInt(product.id.replace('BUNDLE-', ''), 10);
      const pkg = clubEntryPackagesRef.current.find(p => p.id === pkgId);
      if (!pkg) {
        showSnackbar('Paket bulunamadı.', 'error');
        return;
      }
      // Tıklanan adet kadar ayrı grup oluştur (ör. paket 2 kez = 2 kişi = 2 kart)
      const clickCount = Math.max(1, Math.floor(quantity) || 1);
      for (let g = 0; g < clickCount; g++) {
        const groupId = `CG-${pkgId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${g}`;
        pkg.items.forEach(it => {
          const detail = findProductDetailRef.current(it.productId);
          if (!detail) {
            console.warn('[KULÜP PAKET] Ürün detayı bulunamadı, atlanıyor:', it.productName);
            return;
          }
          addToCartRef.current(
            { ...detail, price: detail.salePrice },
            (it.quantity || 1),
            true,
            { groupId, packageId: pkgId, packageName: pkg.name }
          );
        });
      }
      return;
    }

    // Sepet değiştiğinde indirimi sıfırla
    handleRemoveDiscount();

    // SQL'den çekilen ürünlerden orijinal ürünü bul
    const foundInventoryItem = sqlInventoryItems.find(invItem => invItem.id === product.id);
    
    let originalInventoryItem: any;
    
    if (!foundInventoryItem) {
      // Arama sonucu gelen ürünler için parametre ürünü kullan
      if (product.salePrice !== undefined && product.unit && product.vatRate !== undefined) {
        originalInventoryItem = {
          ...product,
          quantity: product.quantity || 0,
          canBeNegative: product.canBeNegative ?? true,
          vatStatus: product.vatStatus || 'excluded'
        };
        
        // Arama sonucu eklenen ürünü sqlInventoryItems'a ekle
        setSqlInventoryItems(prev => {
          const exists = prev.some(item => item.id === product.id);
          if (!exists) {
            return [...prev, originalInventoryItem];
          }
          return prev;
        });
      } else {
        showSnackbar('Ürün bilgileri bulunamadı.', 'error');
        return;
      }
    } else {
      originalInventoryItem = foundInventoryItem;
    }
    
    const productVatRate = originalInventoryItem.vatRate !== undefined ? originalInventoryItem.vatRate : 0;
    // ⚠️ ÖNEMLİ: product.vatStatus API'den güncel gelir, cache'deki değer eski olabilir!
    // Bu yüzden product.vatStatus varsa onu önceliklendir
    const productVatStatus = product.vatStatus || originalInventoryItem.vatStatus;
    console.log('🧾 [KDV DEBUG] originalInventoryItem.vatStatus:', originalInventoryItem.vatStatus, '| product.vatStatus:', product.vatStatus, '| kullanılan:', productVatStatus);
    const itemStockQuantity = originalInventoryItem.quantity !== undefined ? originalInventoryItem.quantity : 0;
    const itemCanBeNegative = originalInventoryItem.canBeNegative === true;
    const productNameForMsg = originalInventoryItem.name || 'Ürün';
    const productUnit = originalInventoryItem.unit || 'adet';

    let priceForCart = product.price;

    // ✅ YENİ: ŞUBE BAZLI FİYATLANDIRMA KONTROLÜ
    // Eğer ürün için useBranchPricing true ise ve şube fiyatı varsa, şube fiyatını kullan
    const useBranchPricing = (originalInventoryItem as any).useBranchPricing === true;
    const branchPrice = branchPricesCache[product.id?.toLowerCase()] || branchPricesCache[product.id];
    
    console.log(`🔍 [ŞUBE FİYAT - addToCart] "${productNameForMsg}" kontrolü:`, {
      useBranchPricing,
      originalInventoryItemUseBranchPricing: (originalInventoryItem as any).useBranchPricing,
      productId: product.id,
      branchPricesCacheKeys: Object.keys(branchPricesCache),
      branchPriceFound: branchPrice ? '✅ BULUNDU' : '❌ YOK',
      branchPrice: branchPrice || null,
      anaFiyat: product.price
    });
    
    // ✅ Şube fiyatı kullanılıyorsa, vatStatus'u da güncelle
    let effectiveVatStatus = productVatStatus;
    
    if (useBranchPricing && branchPrice) {
      // ✅ PAKET MODU veya EKRAN AYARI: packagePrice varsa onu kullan, yoksa salePrice
      if (shouldUsePackagePrice) {
        const branchPackagePrice = branchPrice.packagePrice || branchPrice.PackagePrice;
        if (branchPackagePrice && branchPackagePrice > 0) {
          console.log(`📦 [PAKET FİYAT - addToCart] "${productNameForMsg}" için paket fiyatı kullanılıyor: ${branchPackagePrice} TL (KDV dahil) (isPackageMode=${isPackageMode}, usePackagePrices=${usePackagePrices})`);
          priceForCart = branchPackagePrice;
          effectiveVatStatus = 'included'; // Paket fiyatları da KDV dahil
        } else {
          // Paket fiyatı yoksa salePrice'a fallback
          const branchSalePrice = branchPrice.salePrice || branchPrice.SalePrice;
          if (branchSalePrice) {
            console.log(`⚠️ [PAKET FİYAT - addToCart] "${productNameForMsg}" için paket fiyatı yok, salePrice kullanılıyor: ${branchSalePrice} TL`);
            priceForCart = branchSalePrice;
            effectiveVatStatus = 'included';
          }
        }
      } else {
        // Normal mod: salePrice kullan
        const branchSalePrice = branchPrice.salePrice || branchPrice.SalePrice;
        console.log(`💰 [ŞUBE FİYAT] "${productNameForMsg}" için şube fiyatı kullanılıyor: ${branchSalePrice} TL (KDV dahil)`);
        priceForCart = branchSalePrice;
        // ⚠️ TERMİNOLOJİ TERSİNE ÇEVRİLDİ! Şube fiyatları KDV dahil olduğu için 'included' kullanılıyor
        effectiveVatStatus = 'included'; // included = KDV dahil fiyat = KDV eklenmeyecek
      }
    } else if (useBranchPricing && !branchPrice) {
      console.log(`⚠️ [ŞUBE FİYAT] "${productNameForMsg}" için useBranchPricing=true ama şube fiyatı bulunamadı, ana fiyat kullanılacak: ${priceForCart} TL`);
    }
    // ⚠️ TERMİNOLOJİ UYARISI: AddInventoryItem.tsx'te terminoloji TERS kullanılmış!
    // Kullanıcı "KDV Dahil" seçince → DB'de 'included' → Fiyat KDV DAHİL → calculateTotal'da KDV eklenmez
    // Kullanıcı "KDV Hariç" seçince → DB'de 'excluded' → Fiyat KDV HARİÇ → calculateTotal'da KDV eklenir

    // ✅ YENİ: MENÜ OPSİYON KONTROLÜ
    // Eğer ürünün menüsü varsa, opsiyon seçim modalını aç
    const hasMenu = (originalInventoryItem as any).hasMenu || (product as any).hasMenu || false;
    const hasRecipe = (originalInventoryItem as any).hasRecipe || (product as any).hasRecipe || false;
    
    if (hasMenu && !skipOptionModals && setPendingMenuProduct && setIsMenuOptionModalOpen) {
      console.log(`🍽️ [MENÜ] "${productNameForMsg}" menü ürünü, opsiyon modalı açılıyor...`);
      
      setPendingMenuProduct({
        productId: product.id,
        productName: productNameForMsg,
        productPrice: priceForCart,
        productVatRate: productVatRate,
        productVatStatus: effectiveVatStatus,
        productUnit: productUnit,
        itemCanBeNegative,
        itemStockQuantity,
        hasRecipe,
        hasMenu,
        categoryId: productScreenCategoryMapRef.current[product.id] || originalInventoryItem.categoryId || '',
      });
      
      setTimeout(() => {
        setIsMenuOptionModalOpen(true);
      }, 0);
      
      return; // Menü modalı açıldı, sepete ekleme burada yapılmayacak
    }

    // Eğer ürün birimi kg ise, miktar girişi modalını aç
    if (!skipOptionModals && productUnit.toLowerCase() === 'kg' && quantity === 1) {
      setTempSelectedProduct({
        productInfo: {
          id: product.id,
          name: product.name,
          price: priceForCart,
          vatRate: productVatRate,
          unit: productUnit
        },
        itemCanBeNegative,
        itemStockQuantity
      });
      
      setTimeout(() => {
        setIsWeightInputModalOpen(true);
      }, 0);
      
      return;
    }

    // Sepete eklemek için useCallback kullanıyoruz
    setCart(prevCart => {
      const newCartCleaned = prevCart.map(item => ({ ...item, originalPrice: undefined })); 

      // ✅ YENİ: Aşağıdaki durumlarda her sepete-ekleme tıklaması yeni satır
      // oluşturur (aynı ürün varsa quantity'i artırmaz):
      //   1) `usePackagePrices` ekran ayarı (StockVisibilityJson) açıkken —
      //      farklı adetler için ayrı ayrı yönetim isteniyor.
      //   2) `isPackageMode` (paket hızlı satış modu) — PackageOrderScreen'a
      //      ayrı kalemler olarak aktarılması isteniyor. Telefon siparişinde
      //      kullanıcı aynı üründen 3 adet eklediğinde 3 ayrı satır görmek
      //      istiyor (her biri farklı not/fiyat alabilsin diye).
      // Normal hızlı satışta (her ikisi de kapalıyken) "üst üste birleştir"
      // davranışı korunur.
      // Restoran modu (`isRestaurantMode`) bu hook'a hiç gelmez — `addToCartWithRestaurant`
      // numpad modal akışı üzerinden setCart'ı doğrudan çağırır.
    const splitRowsPerAdd = !!usePackagePrices || isPackageMode || !!clubGroup;
    const existingItemIndex = splitRowsPerAdd
      ? -1
      : newCartCleaned.findIndex(item => item.id === product.id);

      if (existingItemIndex > -1) {
        const currentItemInCart = newCartCleaned[existingItemIndex];
        const currentQuantityInCart = currentItemInCart.quantity;
        const requestedTotalQuantity = currentQuantityInCart + quantity;

        // Stok Kontrolü (Miktar Artırma)
        if (!itemCanBeNegative && itemStockQuantity < requestedTotalQuantity) {
          showSnackbar(`'${productNameForMsg}' için stok yetersiz (Stok: ${itemStockQuantity}, İstenen: ${requestedTotalQuantity}).`, 'warning');
          return prevCart;
        }

        return newCartCleaned.map((item, index) => {
          if (index === existingItemIndex) {
            return { ...item, quantity: item.quantity + quantity }; 
          }
          return item;
        });
      } else {
        // Stok Kontrolü (Yeni Ekleme)
        // ✅ Ayar açıkken aynı ürün birden çok satıra dağılabildiği için stok
        // kontrolünde sepetteki tüm aynı id'li satırların quantity toplamını
        // hesaba katıyoruz — aksi halde 5 stoklu üründe 3+3 ekleme yapılıp
        // limit aşılabilirdi.
        const sameIdQtyAlreadyInCart = splitRowsPerAdd
          ? newCartCleaned
              .filter(item => item.id === product.id)
              .reduce((sum, item) => sum + (item.quantity || 0), 0)
          : 0;
        const requestedTotalQuantity = sameIdQtyAlreadyInCart + quantity;

        if (!itemCanBeNegative && itemStockQuantity < requestedTotalQuantity) {
          const stockMsg = sameIdQtyAlreadyInCart > 0
            ? `'${productNameForMsg}' için stok yetersiz (Stok: ${itemStockQuantity}, Sepetteki: ${sameIdQtyAlreadyInCart}, İstenen toplam: ${requestedTotalQuantity}).`
            : `'${productNameForMsg}' için stok yetersiz (Stok: ${itemStockQuantity}).`;
          showSnackbar(stockMsg, 'warning');
          return prevCart;
        }

        if (quantity > 0) {
          // ✅ FIX: Benzersiz cartItemId oluştur (sepette aynı ürün birden fazla kez seçilebilir sorununu çözer)
          const uniqueCartItemId = `${product.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          
          console.log('🧾 [KDV DEBUG] Sepete eklenen vatStatus:', effectiveVatStatus || 'excluded');
          if (splitRowsPerAdd) {
            console.log(`📦 [AYRI SATIR] "${productNameForMsg}" yeni satır olarak ekleniyor (usePackagePrices=${usePackagePrices}, isPackageMode=${isPackageMode}, sepetteki aynı ürün adedi: ${sameIdQtyAlreadyInCart}).`);
          }
          
          return [...newCartCleaned, { 
            id: product.id,
            name: product.name,
            price: priceForCart,
            quantity: quantity, 
            vatRate: productVatRate,
            // ⚠️ TERMİNOLOJİ: 'included' = KDV dahil fiyat, 'excluded' = KDV hariç fiyat
            vatStatus: effectiveVatStatus || 'excluded', // Varsayılan: excluded (KDV hariç, KDV eklenecek)
            unit: productUnit,
            categoryId: productScreenCategoryMapRef.current[product.id] || originalInventoryItem.categoryId || '',
            cartItemId: uniqueCartItemId, // ✅ Benzersiz cart item ID
            // ✅ YENİ: Reçete ve Menü Bilgileri
            hasRecipe: (originalInventoryItem as any).hasRecipe || (product as any).hasRecipe || false,
            hasMenu: (originalInventoryItem as any).hasMenu || (product as any).hasMenu || false,
            // ✅ KULÜP GİRİŞİ: paket-instance grubu + ürün/hizmet tipi (kart atamada gruplama için)
            ...(clubGroup ? {
              clubGroupId: clubGroup.groupId,
              clubPackageId: clubGroup.packageId,
              clubPackageName: clubGroup.packageName,
            } : {}),
            itemType: (originalInventoryItem as any).itemType || (product as any).itemType || 'PRODUCT',
          }];
        } else {
          return newCartCleaned;
        }
      }
    });
  }, [
    sqlInventoryItems, // ✅ allInventoryItems yerine
    handleRemoveDiscount,
    showSnackbar,
    setTempSelectedProduct,
    setIsWeightInputModalOpen,
    setCart,
    setSqlInventoryItems, // ✅ YENİ: Arama sonucu eklenen ürünleri listeye eklemek için
    branchPricesCache, // ✅ YENİ: Şube fiyatları cache
    isPackageMode, // ✅ YENİ: Paket modu için packagePrice
    usePackagePrices, // ✅ YENİ: Ekran ayarı - şube paket fiyatı kullan
    shouldUsePackagePrice,
  ]);
  // Paket genişletmede recursion için addToCart'ı ref'te güncel tut
  addToCartRef.current = addToCart;

  // ✅ YENİ: Opsiyon seçildikten sonra sepete ekleme
  const addToCartWithOptions = useCallback((
    pendingProduct: any, 
    selectedOptions: any[], 
    totalPriceDifference: number,
    normalItems?: any[] // ✅ Normal menü öğeleri
  ) => {
    if (!pendingProduct) return;
    
    handleRemoveDiscount();
    
    const finalPrice = pendingProduct.productPrice + totalPriceDifference;
    const uniqueCartItemId = `${pendingProduct.productId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    console.log('🍽️ [MENÜ SEPET] Opsiyonlu menü sepete ekleniyor:', {
      productName: pendingProduct.productName,
      basePrice: pendingProduct.productPrice,
      priceDifference: totalPriceDifference,
      finalPrice,
      selectedOptionsCount: selectedOptions.length,
      normalItemsCount: normalItems?.length || 0
    });
    
    setCart(prevCart => {
      const newCartCleaned = prevCart.map(item => ({ ...item, originalPrice: undefined }));
      
      // Stok Kontrolü
      if (!pendingProduct.itemCanBeNegative && pendingProduct.itemStockQuantity < 1) {
        showSnackbar(`'${pendingProduct.productName}' için stok yetersiz (Stok: ${pendingProduct.itemStockQuantity}).`, 'warning');
        return prevCart;
      }
      
      return [...newCartCleaned, {
        id: pendingProduct.productId,
        name: pendingProduct.productName,
        price: finalPrice,
        quantity: 1,
        vatRate: pendingProduct.productVatRate,
        vatStatus: pendingProduct.productVatStatus || 'excluded',
        unit: pendingProduct.productUnit,
        categoryId: pendingProduct.categoryId || '',
        cartItemId: uniqueCartItemId,
        hasRecipe: pendingProduct.hasRecipe,
        hasMenu: pendingProduct.hasMenu,
        // ✅ YENİ: Seçilen opsiyonlar (backend'e gönderilecek)
        selectedOptions: selectedOptions,
        totalPriceDifference: totalPriceDifference,
        // ✅ YENİ: Normal menü öğeleri
        menuNormalItems: normalItems || [],
      }];
    });
    
    showSnackbar(`${pendingProduct.productName} sepete eklendi`, 'success');
  }, [handleRemoveDiscount, setCart, showSnackbar]);

  // Ürün resimlerini yükle ve önbellekle
  const loadAndCacheProductImages = useCallback(() => {
    if (!companyId || sqlInventoryItems.length === 0) return;
    
    let existingCache: {[productId: string]: string} = {};
    let cacheTimestamp = 0;
    
    try {
      const cachedImagesJson = localStorage.getItem(`productImagesCache_${companyId}`);
      const cachedTimestampStr = localStorage.getItem(`productImagesTimestamp_${companyId}`);
      
      if (cachedImagesJson && cachedTimestampStr) {
        existingCache = JSON.parse(cachedImagesJson);
        cacheTimestamp = parseInt(cachedTimestampStr, 10);
      }
    } catch (error) {
      // Cache okuma hatası - devam et
    }
    
    const newCache: {[productId: string]: string} = {...existingCache};
    let cacheUpdated = false;
    
    sqlInventoryItems.forEach(item => {
      if (item.imageUrl && (!existingCache[item.id] || existingCache[item.id] !== item.imageUrl)) {
        newCache[item.id] = item.imageUrl;
        cacheUpdated = true;
      }
    });
    
    if (cacheUpdated || Object.keys(existingCache).length === 0) {
      setProductImagesCache(newCache);
      const timestamp = Date.now();
      setImagesCacheTimestamp(timestamp);
      
      try {
        localStorage.setItem(`productImagesCache_${companyId}`, JSON.stringify(newCache));
        localStorage.setItem(`productImagesTimestamp_${companyId}`, timestamp.toString());
      } catch (error) {
        // Cache yazma hatası - devam et
      }
    } else {
      setProductImagesCache(existingCache);
      setImagesCacheTimestamp(cacheTimestamp);
    }
  }, [companyId, sqlInventoryItems]);

  // Kategori değişikliği handler
  const handleCategoryChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setSelectedCategory(newValue);
    
    // ✅ YENİ: Kategori değiştirilince fokuslan
    if (focusSearchBar) {
      setTimeout(() => {
        focusSearchBar();
      }, 100);
    }
  }, [focusSearchBar]);

  // ─────────────────────────────────────────────────────────
  // ✅ ŞUBE FİYATI GARANTİ ÇÖZÜCÜ (race-condition fix)
  //
  // SORUN: Şube fiyatları (branchPricesCache) ürünlerden SONRA asenkron
  // yüklenir. Cache dolmadan eklenen ürünler sessizce ANA fiyata düşüyordu;
  // cache dolduktan sonra eklenenler ŞUBE fiyatı alıyordu. Sonuç: aynı ürün
  // adisyonda bazen ana, bazen şube fiyatıyla çıkıyordu.
  //
  // ÇÖZÜM: Sepete eklerken (fiyatın SQL'e UnitPrice olarak donduğu an) bu
  // çözücü çağrılır. useBranchPricing=true ürünün şube fiyatı cache'de yoksa
  // ANINDA (await) tek ürünlük bulk-by-branch ile çekilir. Böylece şube
  // fiyatı tanımlıysa KESİNLİKLE kullanılır; gerçekten tanımlı değilse ana
  // fiyata düşülür (doğru fallback).
  //
  // Dönüş: { price, vatStatus } — şube fiyatı uygulandıysa vatStatus
  // 'included' (KDV dahil). Ürün bulunamazsa null.
  // ─────────────────────────────────────────────────────────
  const resolveBranchPrice = useCallback(async (
    productId: string,
  ): Promise<{ price: number; vatStatus: 'included' | 'excluded'; usedBranchPrice: boolean } | null> => {
    const item = sqlInventoryItems.find(i => i.id === productId);
    if (!item) return null;

    let price = item.salePrice || 0;
    let vatStatus: 'included' | 'excluded' = ((item as any).vatStatus as 'included' | 'excluded') || 'excluded';
    let usedBranchPrice = false;

    if ((item as any).useBranchPricing === true && companyId && branchId) {
      const key = productId?.toLowerCase();
      let bp = branchPricesCache[key] || branchPricesCache[productId];

      // Cache'de yoksa ANINDA çek (yarış durumunu kapatır)
      if (!bp) {
        try {
          console.log(`⏳ [ŞUBE FİYAT - resolve] "${item.name}" cache'de yok, anında çekiliyor... (şube: ${branchId})`);
          const prices = await InventoryService.getBulkBranchPrices(companyId, branchId, [productId]);
          bp = prices[key] || prices[productId];
          if (prices && Object.keys(prices).length > 0) {
            setBranchPricesCache(prev => ({ ...prev, ...prices }));
          }
        } catch (e) {
          console.warn('⚠️ [ŞUBE FİYAT - resolve] Anlık şube fiyatı çekme hatası:', e);
        }
      }

      if (bp) {
        if (shouldUsePackagePrice) {
          const pkg = bp.packagePrice || bp.PackagePrice;
          if (pkg && pkg > 0) {
            price = pkg; vatStatus = 'included'; usedBranchPrice = true;
          } else {
            const sp = bp.salePrice || bp.SalePrice;
            if (sp) { price = sp; vatStatus = 'included'; usedBranchPrice = true; }
          }
        } else {
          const sp = bp.salePrice || bp.SalePrice;
          if (sp) { price = sp; vatStatus = 'included'; usedBranchPrice = true; }
        }
      } else {
        console.warn(`⚠️ [ŞUBE FİYAT - resolve] "${item.name}" useBranchPricing=true ama bu şube için fiyat tanımlı değil → ana fiyat: ${price} TL`);
      }
    }

    return { price, vatStatus, usedBranchPrice };
  }, [sqlInventoryItems, branchPricesCache, companyId, branchId, shouldUsePackagePrice]);

  return {
    selectedCategory,
    setSelectedCategory,
    displayableCategories,
    filteredProducts,
    addToCart,
    addToCartWithOptions, // ✅ YENİ: Opsiyonlu menü için
    productImagesCache,
    selectedWarehouseIds,
    setSelectedWarehouseIds,
    activeDisplayWarehouseId,
    setActiveDisplayWarehouseId,
    handleCategoryChange,
    loadAndCacheProductImages,
    sqlInventoryItems,
    getScreenCategoryId: (productId: string) => productScreenCategoryMapRef.current[productId] || '',
    resolveBranchPrice, // ✅ YENİ: Şube fiyatı garanti çözücü (race-condition fix)
  };
};

